/**
 * ConfirmationModal - Reusable confirmation dialog component
 *
 * Used for delete confirmations and other destructive actions
 * throughout the application.
 *
 * Props:
 * - show (boolean, required): Controls modal visibility
 * - title (string, required): Modal header title
 * - message (string): Main message text
 * - itemName (string): Name of item being deleted (highlighted)
 * - itemCount (number): For bulk delete, number of items
 * - confirmText (string): Confirm button text (default: "Delete")
 * - cancelText (string): Cancel button text (default: "Cancel")
 * - onConfirm (function, required): Called when user confirms
 * - onCancel (function, required): Called when user cancels
 * - isLoading (boolean): Shows spinner on confirm button
 * - variant (string): Button variant: "danger", "warning", or "primary"
 * - warningText (string): Warning text below the message (default: "This action cannot be undone.")
 */
import React from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FiAlertTriangle } from 'react-icons/fi';
import styles from './ConfirmationModal.module.css';

const ConfirmationModal = ({
  show,
  title,
  message = null,
  itemName = null,
  itemCount = null,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  variant = 'danger',
  warningText = 'This action cannot be undone.',
}) => {
  const handleClose = () => {
    if (!isLoading) {
      onCancel();
    }
  };

  const renderMessage = () => {
    if (itemCount && itemCount > 1) {
      return (
        <p className={styles.message}>
          {message || 'You are about to permanently delete'}{' '}
          <span className={styles.itemCount}>{itemCount} items</span>.
        </p>
      );
    }

    if (itemName) {
      return (
        <>
          <p className={styles.message}>{message || 'Are you sure you want to delete this item?'}</p>
          <p className={styles.itemNameLine}>
            <span className={styles.itemName}>"{itemName}"</span>
          </p>
        </>
      );
    }

    return <p className={styles.message}>{message || 'Are you sure you want to proceed?'}</p>;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className={styles.confirmationModal}
      backdrop={isLoading ? 'static' : true}
      keyboard={!isLoading}
    >
      <Modal.Header closeButton={!isLoading} className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <FiAlertTriangle className={styles.warningIcon} />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        {renderMessage()}
        <p className={styles.warning}>{warningText}</p>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={isLoading}
          className={styles.cancelBtn}
        >
          {cancelText}
        </Button>
        <Button
          variant={variant}
          onClick={onConfirm}
          disabled={isLoading}
          className={styles.confirmBtn}
        >
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className={styles.spinner}
              />
              Processing...
            </>
          ) : (
            confirmText
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmationModal;
